import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Menu, MenuItem, Tooltip, Typography, Box } from '@mui/material';

// Función para obtener la URL de la bandera
const getFlagUrl = (countryCode) => {
  // Bandera catalana (Senyera) - 4 barras rojas sobre fondo amarillo
  if (countryCode === 'ca') {
    // SVG de la senyera catalana: fondo amarillo con 4 barras rojas horizontales iguales
    const senyeraSvg = `<svg width="20" height="15" viewBox="0 0 20 15" xmlns="http://www.w3.org/2000/svg">
      <rect width="20" height="15" fill="#FCDD09"/>
      <rect width="20" height="3" y="0" fill="#DA020E"/>
      <rect width="20" height="3" y="4" fill="#DA020E"/>
      <rect width="20" height="3" y="8" fill="#DA020E"/>
      <rect width="20" height="3" y="12" fill="#DA020E"/>
    </svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(senyeraSvg)}`;
  }
  
  const flagMap = {
    'es': 'es',
    'en': 'gb',
    'fr': 'fr',
    'de': 'de',
    'pt': 'pt',
    'zh': 'cn',
    'ru': 'ru',
    'ja': 'jp',
    'sv': 'se',
    'nb': 'no',
    'fi': 'fi',
  };
  const code = flagMap[countryCode] || 'gb';
  return `https://flagcdn.com/w20/${code}.png`;
};

const languages = [
  { code: 'es', name: 'Español', countryCode: 'es' },
  { code: 'en', name: 'English', countryCode: 'gb' },
  { code: 'ca', name: 'Català', countryCode: 'ca' },
  { code: 'fr', name: 'Français', countryCode: 'fr' },
  { code: 'de', name: 'Deutsch', countryCode: 'de' },
  { code: 'pt', name: 'Português', countryCode: 'pt' },
  { code: 'zh', name: '中文', countryCode: 'zh' },
  { code: 'ru', name: 'Русский', countryCode: 'ru' },
  { code: 'ja', name: '日本語', countryCode: 'ja' },
  { code: 'sv', name: 'Svenska', countryCode: 'sv' },
  { code: 'nb', name: 'Norsk', countryCode: 'nb' },
  { code: 'fi', name: 'Suomi', countryCode: 'fi' },
];

const LanguageSelector = () => {
  const { i18n, t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Obtener el código de idioma base (ej: "ca-ES" -> "ca")
  const getLanguageCode = (lang) => {
    if (!lang) return 'en';
    return lang.split('-')[0].toLowerCase();
  };

  const currentLangCode = getLanguageCode(i18n.language);
  const currentLanguage = languages.find(lang => lang.code === currentLangCode) || languages[1]; // Default to English

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    handleClose();
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '13px', 
            color: 'rgba(0, 0, 0, 0.6)',
            whiteSpace: 'nowrap'
          }}
        >
          {t('common.language')}:
        </Typography>
        <Tooltip title={currentLanguage.name}>
          <IconButton
            onClick={handleClick}
            sx={{
              color: 'rgba(0, 0, 0, 0.6)',
              padding: '4px',
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
            size="small"
          >
            <img
              src={getFlagUrl(currentLanguage.countryCode)}
              alt={currentLanguage.name}
              style={{
                width: '20px',
                height: '15px',
                objectFit: 'cover',
                borderRadius: '2px',
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            selected={currentLangCode === lang.code}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'rgba(102, 188, 95, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(102, 188, 95, 0.2)',
                },
              },
            }}
          >
            <img
              src={getFlagUrl(lang.countryCode)}
              alt={lang.name}
              style={{
                width: '20px',
                height: '15px',
                marginRight: '8px',
                objectFit: 'cover',
                borderRadius: '2px',
              }}
            />
            {lang.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSelector;

